import React, { useEffect, useState } from "react";
import { useInjection } from "@presentation/components/dependencyInjection";
import { TeamProjectActionsService } from "@application/useCases/workspace/TeamProjectActionsService";
import { ListJobsService } from "@application/useCases/job/ListJobTasksService";
import { projectScope } from "@domain/valueObjects/JobDiscoveryScope";
import { extractProblemDetails } from "@presentation/common/ErrorHelpers";
import { useNotifications } from "@presentation/components/notifications";
import { ConfirmDeleteDialog } from "@presentation/components/common/ConfirmDeleteDialog";
import { useRecentlyVisitedStore } from "@presentation/stores/RecentlyVisitedStore";
import { useWorkspaceSuccessNotification } from "@presentation/components/notifications/useWorkspaceSuccessNotification";

interface DeleteProjectDialogProps {
  teamId: string;
  projectId: string;
  projectName: string;
  onClose: () => void;
  onSuccess: () => void;
}

/**
A project can only be deleted once it has zero jobs (`Projects.CannotDeleteProjectWithJobs`) - this
fetches just the job count up front (page size 1, reading `totalCount`) so the blocked reason is
explained inline instead of only surfacing as a server error after the user already clicked Delete.
*/
export const DeleteProjectDialog: React.FC<DeleteProjectDialogProps> = ({ teamId, projectId, projectName, onClose, onSuccess }) => {
  const teamProjectActionsService = useInjection(TeamProjectActionsService);
  const listJobsService = useInjection(ListJobsService);
  const { pushNotification } = useNotifications();
  const notifySuccess = useWorkspaceSuccessNotification();
  const forgetProject = useRecentlyVisitedStore((state) => state.forgetProject);
  const [jobCount, setJobCount] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    let isCancelled = false;
    void (async () => {
      try {
        const pagedJobList = await listJobsService.getJobListAsync(projectScope(projectId), 1, 1);
        if (!isCancelled) setJobCount(pagedJobList.totalCount ?? 0);
      } catch {
        if (!isCancelled) setJobCount(0);
      }
    })();
    return () => {
      isCancelled = true;
    };
  }, [projectId, listJobsService]);

  const handleConfirm = async () => {
    setIsDeleting(true);
    try {
      await teamProjectActionsService.deleteProjectAsync(teamId, projectId);
      notifySuccess("project", "deleted");
      forgetProject(projectId);
      onSuccess();
      onClose();
    } catch (error) {
      pushNotification(extractProblemDetails(error).detail ?? extractProblemDetails(error).title ?? "Could not delete this project.", "error", 10_000);
    } finally {
      setIsDeleting(false);
    }
  };

  const isBlocked = (jobCount ?? 0) > 0;

  return (
    <ConfirmDeleteDialog title="Delete project" isBlocked={isBlocked} isDeleting={isDeleting} onCancel={onClose} onConfirm={handleConfirm}>
      <p style={{ margin: 0 }}>
        Delete project <strong>{projectName}</strong>? This cannot be undone.
      </p>
      {jobCount === null ? (
        <p style={{ margin: 0, color: "#64748b" }}>Checking this project&apos;s jobs...</p>
      ) : (
        isBlocked && (
          <p style={{ margin: 0, fontWeight: 600, color: "#7f1d1d" }}>
            This project still has {jobCount} job{jobCount === 1 ? "" : "s"} and can&apos;t be deleted until they&apos;re deleted first.
          </p>
        )
      )}
    </ConfirmDeleteDialog>
  );
};
